/**
 * Firestore Operation Monitor
 * Tracks Firestore reads, writes, and deletes per page load
 * Helps monitor usage against Firebase free tier limits
 */

class FirestoreMonitor {
  constructor() {
    this.operations = [];
    this.currentPage = null;
    this.pageStartTime = null;
    this.enabled = true;
    
    // Track operations by type
    this.stats = {
      reads: 0,
      writes: 0,
      deletes: 0,
      functionInvocations: 0
    };
    
    this.setupPageTracking();
  }

  setupPageTracking() {
    // Track page changes
    if (typeof window !== 'undefined') {
      // Track initial page
      this.setCurrentPage(window.location.pathname);
      
      // Track route changes (for React Router)
      window.addEventListener('popstate', () => {
        this.setCurrentPage(window.location.pathname);
      });
      
      // Override pushState and replaceState to track programmatic navigation
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;
      
      const self = this;
      window.history.pushState = function(...args) {
        originalPushState.apply(window.history, args);
        self.setCurrentPage(window.location.pathname);
      };
      
      window.history.replaceState = function(...args) {
        originalReplaceState.apply(window.history, args);
        self.setCurrentPage(window.location.pathname);
      };
    }
  }

  setCurrentPage(pathname) {
    // Reset stats for new page
    if (this.currentPage !== pathname) {
      this.currentPage = pathname;
      this.pageStartTime = Date.now();
      this.stats = {
        reads: 0,
        writes: 0,
        deletes: 0,
        functionInvocations: 0
      };
      this.operations = [];
    }
  }

  trackRead(collection, docId = null, count = 1) {
    if (!this.enabled) return;
    
    this.stats.reads += count;
    this.operations.push({
      type: 'read',
      collection,
      docId,
      count,
      timestamp: Date.now(),
      page: this.currentPage
    });
  }

  trackWrite(collection, docId = null, count = 1) {
    if (!this.enabled) return;
    
    this.stats.writes += count;
    this.operations.push({
      type: 'write',
      collection,
      docId,
      count,
      timestamp: Date.now(),
      page: this.currentPage
    });
  }

  trackDelete(collection, docId = null, count = 1) {
    if (!this.enabled) return;
    
    this.stats.deletes += count;
    this.operations.push({
      type: 'delete',
      collection,
      docId,
      count,
      timestamp: Date.now(),
      page: this.currentPage
    });
  }

  trackFunctionInvocation(functionName) {
    if (!this.enabled) return;
    
    this.stats.functionInvocations += 1;
    this.operations.push({
      type: 'function',
      functionName,
      timestamp: Date.now(),
      page: this.currentPage
    });
  }

  // Track operations from API response headers
  trackFromApiResponse(reads, writes, deletes, endpoint) {
    if (!this.enabled) return;
    
    if (reads > 0) this.trackRead('api', null, reads);
    if (writes > 0) this.trackWrite('api', null, writes);
    if (deletes > 0) this.trackDelete('api', null, deletes);
    if (endpoint) this.trackFunctionInvocation(endpoint);
  }

  getPageStats() {
    const pageTime = this.pageStartTime ? Date.now() - this.pageStartTime : 0;
    
    return {
      page: this.currentPage || 'unknown',
      timeOnPage: pageTime,
      ...this.stats,
      operations: this.operations.filter(op => op.page === this.currentPage)
    };
  }

  getReport() {
    const pageStats = this.getPageStats();
    
    // Group operations by collection
    const byCollection = {};
    this.operations.forEach(op => {
      if (op.collection) {
        if (!byCollection[op.collection]) {
          byCollection[op.collection] = { reads: 0, writes: 0, deletes: 0 };
        }
        if (op.type === 'read') byCollection[op.collection].reads += op.count || 1;
        if (op.type === 'write') byCollection[op.collection].writes += op.count || 1;
        if (op.type === 'delete') byCollection[op.collection].deletes += op.count || 1;
      }
    });

    return {
      currentPage: pageStats.page,
      timeOnPage: `${(pageStats.timeOnPage / 1000).toFixed(2)}s`,
      totals: {
        reads: pageStats.reads,
        writes: pageStats.writes,
        deletes: pageStats.deletes,
        functionInvocations: pageStats.functionInvocations,
        total: pageStats.reads + pageStats.writes + pageStats.deletes
      },
      byCollection,
      operations: pageStats.operations
    };
  }

  printReport() {
    const report = this.getReport();
    
    console.group(`🔥 Firestore Usage Report - ${report.currentPage}`);
    console.log(`⏱️  Time on page: ${report.timeOnPage}`);
    console.log('');
    console.log('📊 Total Operations:');
    console.log(`   Reads: ${report.totals.reads}`);
    console.log(`   Writes: ${report.totals.writes}`);
    console.log(`   Deletes: ${report.totals.deletes}`);
    console.log(`   Function Invocations: ${report.totals.functionInvocations}`);
    console.log(`   Total: ${report.totals.total}`);
    console.log('');
    
    if (Object.keys(report.byCollection).length > 0) {
      console.log('📁 Operations by Collection:');
      Object.entries(report.byCollection).forEach(([collection, ops]) => {
        const total = ops.reads + ops.writes + ops.deletes;
        console.log(`   ${collection}:`, {
          reads: ops.reads,
          writes: ops.writes,
          deletes: ops.deletes,
          total
        });
      });
      console.log('');
    }
    
    // Estimate monthly usage (assuming average user visits 10 pages per session, 30 sessions/month)
    const estimatedMonthly = {
      reads: report.totals.reads * 10 * 30,
      writes: report.totals.writes * 10 * 30,
      deletes: report.totals.deletes * 10 * 30,
      functionInvocations: report.totals.functionInvocations * 10 * 30
    };
    
    console.log('📈 Estimated Monthly Usage (per user, 10 pages/session, 30 sessions/month):');
    console.log(`   Reads: ~${estimatedMonthly.reads.toLocaleString()}`);
    console.log(`   Writes: ~${estimatedMonthly.writes.toLocaleString()}`);
    console.log(`   Deletes: ~${estimatedMonthly.deletes.toLocaleString()}`);
    console.log(`   Function Invocations: ~${estimatedMonthly.functionInvocations.toLocaleString()}`);
    console.log('');
    
    // Compare to free tier limits
    const freeTierLimits = {
      reads: 50000,
      writes: 20000,
      deletes: 20000,
      functionInvocations: 2000000
    };
    
    console.log('⚠️  Free Tier Limits:');
    console.log(`   Reads: ${freeTierLimits.reads.toLocaleString()}/month`);
    console.log(`   Writes: ${freeTierLimits.writes.toLocaleString()}/month`);
    console.log(`   Deletes: ${freeTierLimits.deletes.toLocaleString()}/month`);
    console.log(`   Function Invocations: ${freeTierLimits.functionInvocations.toLocaleString()}/month`);
    console.log('');
    
    // Calculate how many users can use the site before hitting limits
    const maxUsers = {
      reads: Math.floor(freeTierLimits.reads / estimatedMonthly.reads),
      writes: Math.floor(freeTierLimits.writes / estimatedMonthly.writes),
      deletes: Math.floor(freeTierLimits.deletes / estimatedMonthly.deletes),
      functionInvocations: Math.floor(freeTierLimits.functionInvocations / estimatedMonthly.functionInvocations)
    };
    
    const limitingFactor = Math.min(...Object.values(maxUsers).filter(v => v > 0));
    
    console.log('👥 Estimated Capacity (users/month before hitting free tier):');
    console.log(`   Based on reads: ~${maxUsers.reads.toLocaleString()} users`);
    console.log(`   Based on writes: ~${maxUsers.writes.toLocaleString()} users`);
    console.log(`   Based on deletes: ~${maxUsers.deletes.toLocaleString()} users`);
    console.log(`   Based on functions: ~${maxUsers.functionInvocations.toLocaleString()} users`);
    console.log(`   ⚠️  Limiting factor: ~${limitingFactor.toLocaleString()} users/month`);
    
    console.groupEnd();
    
    return report;
  }

  clear() {
    this.operations = [];
    this.stats = {
      reads: 0,
      writes: 0,
      deletes: 0,
      functionInvocations: 0
    };
  }
}

// Create singleton instance
const firestoreMonitor = new FirestoreMonitor();

// Expose to window for debugging (initialization happens in index.js)
if (typeof window !== 'undefined') {
  window.firestoreMonitor = firestoreMonitor;
  
  // Auto-print report after page load if enabled
  window.addEventListener('load', () => {
    setTimeout(() => {
      if (firestoreMonitor.enabled) {
        console.log('🔥 Page load complete. Generating Firestore usage report...');
        firestoreMonitor.printReport();
      }
    }, 5000); // Wait 5 seconds after page load
  });
}

export default firestoreMonitor;

